import SearchableSelect from '../../components/ui/SearchableSelect';
import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import { convertQuantity } from "../../utils/units";
import { useState, useMemo, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { useAuth } from "../Context/AuthContext";
import ProductionBatchDetails from './ProductionBatchDetails';
import ProductionJobs from './ProductionJobs';

export default function ProductionBatches() {
  const [legacy,setLegacy]=useState(false);
  return legacy?<LegacyProductionBatches onWorkspace={()=>setLegacy(false)}/>:<ProductionJobs onLegacy={()=>setLegacy(true)}/>;
}

function LegacyProductionBatches({onWorkspace}) {
  const [detail,setDetail]=useState(null);
  const [departments,setDepartments]=useState([]);
  const [producingDepartment,setProducingDepartment]=useState('');
  const [actionBusy,setActionBusy]=useState(false);
  const viewDetails=async id => {
    try {
      const res=await fetch(`/api/production/details/${id}`,{credentials:'include'});
      const data=await res.json();
      if (!res.ok || data.status!=='success') throw new Error(data.message || data.detail || 'Unable to load batch details');
      setDetail(data.data);
    } catch(e) { alert(e.message); }
  };
  const [batches, setBatches] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const { user, branchId } = useAuth();

  const [showModal, setShowModal] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [batchQty, setBatchQty] = useState("");
  const [assignedStaff, setAssignedStaff] = useState(null);
  const [shift, setShift] = useState("Morning");
  const [narration, setNarration] = useState("");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedBatch, setSelectedBatch] = useState(null);

  const [handoverBatchId, setHandoverBatchId] = useState(null);
  const [handoverStaff, setHandoverStaff] = useState("");
  const [handoverShift, setHandoverShift] = useState("");
  const [handoverNote, setHandoverNote] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    fetchInventory();
    fetchBatches();
    fetchRecipes();
    fetchStaff();
    fetchBranches();
    fetch('/api/departments',{credentials:'include'}).then(r => r.json()).then(data => { if(data.status==='success') setDepartments(data.data); }).catch(() => {});
  }, []);

  const fetchInventory = async () => {
    const res = await fetch("/api/inventory/items", {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") setInventory(data.data);
  };

  const fetchBranches = async () => {
    try {
      const res = await fetch("/api/branches", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setBranches(data.data);
      }
    } catch (err) {
      console.error("Branch fetch error:", err);
    }
  };

  useEffect(() => {
    if (user?.role !== "super_admin") {
      // no UI change, just force branch
      setSelectedBranch(branchId);
    }
  }, [user, branchId]);

  const fetchBatches = async () => {
    const res = await fetch("/api/production/list", {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") {
      setBatches(
        data.data.map((b) => ({
          ...b,
          item: b.product_item_id, // map if needed later
        })),
      );
    }
  };

  const fetchRecipes = async () => {
    try {
      const res = await fetch("/api/recipes", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setRecipes(data.data);
      } else {
        console.log("Recipe fetch failed:", data.message);
      }
    } catch (err) {
      console.error("Recipe error:", err);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await fetch("/api/staff", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        // 🔥 format for your UI
        const formatted = data.data
          .filter((s) => s.status === "Active") // optional but recommended
          .map((s) => ({
            id: s.id,
            name: s.name,
            role: s.role,
            branch_id: s.branch_id,
          }));

        setStaffList(formatted);
      } else {
        console.log("Staff fetch failed:", data.message);
      }
    } catch (err) {
      console.error("Staff error:", err);
    }
  };

  const deleteBatch = async (id) => {
    if (!window.confirm("Delete this batch?")) return;

    try {
      const res = await fetch(`/api/production/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message);
        return;
      }

      fetchBatches();
    } catch (err) {
      console.error(err);
    }
  };

  const updateBatch = async () => {
    if (!selectedBatch) return;

    try {
      const payload = {
        batch_id: selectedBatch.id,
        quantity: Number(batchQty),
        staff_id: assignedStaff,
        shift,
        narration,
        department_id:Number(producingDepartment),
      };

      const res = await fetch(
        `/api/production/${selectedBatch.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message);
        return;
      }

      fetchBatches();

      // reset
      setShowModal(false);
      setSelectedBatch(null);
      setBatchQty("");
      setAssignedStaff(null);
      setShift("Morning");
      setNarration("");
    } catch (err) {
      console.error(err);
    }
  };

  const getItemId = (name) => {
    const item = inventory.find((i) => i.name === name);
    return item?.id || null;
  };

  const deductionPreview = useMemo(() => {
    if (!selectedRecipe || !batchQty) return [];

    return selectedRecipe.ingredients.map((ing) => {
      const inventoryItem = inventory.find((i) => i.id === ing.item_id);
      let available = 0;
      try {
        if (Number(inventoryItem?.stock) > 0) {
          available = convertQuantity(inventoryItem.stock, inventoryItem.unit, ing.unit);
        }
      } catch (error) {
        // Incompatible packaging units cannot be assumed to contain a fixed quantity.
        available = 0;
      }

      const required = (ing.qty / selectedRecipe.baseQty) * batchQty;

      return { ...ing, required, available, department: inventoryItem?.department || 'Unassigned department' };
    });
  }, [selectedRecipe, batchQty, inventory]);

  const hasInsufficientStock = deductionPreview.some(
    (i) => i.required > i.available,
  );

  const createBatch = async () => {
    if (!selectedRecipe || !batchQty || hasInsufficientStock || !assignedStaff)
      return;

    if (!selectedBranch && user?.role === "super_admin") {
      alert("Please select branch");
      return;
    }

    try {
      const payload = {
        product_item_id: selectedRecipe.product_item_id,
        department_id:Number(producingDepartment),
        quantity: Number(batchQty),
        unit: selectedRecipe.baseUnit,
        branch_id: user?.role === "super_admin" ? selectedBranch : branchId,
        staff_id: assignedStaff,
        shift,
        narration,
        ingredients: selectedRecipe.ingredients.map((ing) => ({
          item_id: ing.item_id,
          required_qty: (ing.qty / selectedRecipe.baseQty) * Number(batchQty),
          unit: ing.unit,
        })),
      };

      const res = await fetch("/api/production/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status !== "success") {
        alert(data.message);
        return;
      }

      fetchBatches();

      setShowModal(false);
      setSelectedRecipe(null);
      setBatchQty("");
      setAssignedStaff("");
      setShift("Morning");
      setNarration("");
      setSelectedBranch("");
    } catch (err) {
      console.error(err);
    }
  };

  const startBatch = async (id) => {
    await changeBatchState(id,'start');
  };

  const completeBatch = async (id) => {
    await changeBatchState(id,'complete');
  };
  const changeBatchState=async (id,action) => {
    if (actionBusy || !window.confirm(action==='start' ? 'Start production and consume the listed ingredients?' : 'Complete production and receive the planned finished quantity?')) return;
    setActionBusy(true);
    try {
      const res=await fetch(`/api/production/${action}/${id}`,{method:'POST',credentials:'include'});
      const data=await res.json();
      if (!res.ok || data.status!=='success') throw new Error(data.message || data.detail || 'Production action failed');
      await Promise.all([fetchBatches(),fetchInventory()]);
    } catch(e) { alert(e.message); } finally { setActionBusy(false); }
  };

  const handoverBatch = async (batchId, toStaff, toShift, note) => {
    await fetch("/api/production/handover", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        batch_id: batchId,
        to_staff: toStaff,
        to_shift: toShift,
        note,
      }),
    });

    fetchBatches();
  };

  const totalPages = Math.ceil(batches.length / itemsPerPage);

  const paginatedBatches = batches.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div><h1 className="text-2xl font-serif">Legacy Production Batches</h1><button className="mt-2 text-sm text-amber-400" onClick={onWorkspace}>← Material issue production workspace</button></div>
          <PermissionButton permissionModule="production"
            onClick={() => setShowModal(true)}
            className="px-5 py-2 rounded-xl bg-amber-400 text-black font-medium"
          >
            + New Batch
          </PermissionButton>
        </div>

        {/* TABLE */}
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-gray-400">
              <tr>
                <th className="px-6 py-4 text-left">Batch ID</th>
                <th className="px-6 py-4 text-left">Item</th>
                <th className="px-6 py-4 text-left">Department / Branch</th>
                <th className="px-6 py-4 text-left">Quantity</th>
                <th className="px-6 py-4 text-left">Date</th>
                <th className="px-6 py-4 text-left">Staff</th>
                <th className="px-6 py-4 text-left">Shift</th>
                <th className="px-6 py-4 text-left">Status</th>
                <th className="px-6 py-4 text-left">Action</th>
              </tr>
            </thead>

            <tbody>
              {paginatedBatches.map((b) => (
                <tr key={b.id} className="border-t border-white/10">
                  <td className="px-6 py-4">{b.id}</td>
                  <td>
                    {inventory.find((i) => i.id === b.product_item_id)?.name ||
                      "-"}
                  </td>
                  <td className="px-6 py-4">{b.department || '—'}<p className="text-xs text-gray-400">{b.branch || '—'}</p></td>
                  <td className="px-6 py-4 text-left">
                    {b.quantity} {b.unit}
                  </td>
                  <td className="px-6 py-4 text-left">
                    {new Date(b.date).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="px-6 py-4 text-left">
                    {staffList.find((s) => s.id === b.staff_id)?.name || "-"}
                  </td>
                  <td className="px-6 py-4 text-left">{b.shift}</td>
                  <td className="px-6 py-4 text-left">
                    <span
                      className={`px-3 py-1 text-xs rounded-full border ${
                        b.status === "queued"
                          ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                          : b.status === "running"
                            ? "bg-amber-400/20 text-amber-400 border-amber-400/30"
                            : "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                      }`}
                    >
                      {b.status.toUpperCase()}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-left"><TableActions>
                    <div className="flex gap-2 justify-center flex-wrap">
                      <PermissionButton permissionModule="production" onClick={() => viewDetails(b.id)} className="px-3 py-1 text-xs rounded-lg border border-white/20">View batch & stock movements</PermissionButton>
                      {/* START */}
                      {b.status === "queued" && (
                        <PermissionButton permissionModule="production"
                          onClick={() => startBatch(b.id)}
                          disabled={actionBusy}
                          className="px-3 py-1 text-xs rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30"
                        >
                          Start
                        </PermissionButton>
                      )}

                      {/* COMPLETE */}
                      {b.status === "running" && (
                        <PermissionButton permissionModule="production"
                          onClick={() => completeBatch(b.id)}
                          disabled={actionBusy}
                          className="px-3 py-1 text-xs rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                        >
                          Complete
                        </PermissionButton>
                      )}

                      {/* HANDOVER */}
                      <PermissionButton permissionModule="production"
                        disabled={b.status!=='running' || actionBusy}
                        onClick={() => {
                          setHandoverBatchId(b.id);
                          setHandoverShift("Evening");
                        }}
                        className="px-3 py-1 text-xs rounded-lg bg-amber-400/20 text-amber-400 border border-amber-400/30 hover:bg-amber-400/30"
                      >
                        Handover
                      </PermissionButton>

                      {b.status === "queued" && (
                        <PermissionButton permissionModule="production"
                          onClick={() => {
                            setSelectedBatch(b);
                            setBatchQty(b.quantity);
                            setAssignedStaff(b.staff_id);
                            setShift(b.shift);
                            setNarration(b.narration || '');
                            setProducingDepartment(b.department_id || '');

                            setShowModal(true);
                          }}
                          className="px-3 py-1 text-xs rounded-lg border border-yellow-400 text-yellow-400 hover:bg-yellow-400/10"
                        >
                          Edit
                        </PermissionButton>
                      )}

                      {/* DELETE */}
                      <PermissionButton permissionModule="production" permissionAction="delete"
                        disabled={b.status!=='queued' || actionBusy}
                        onClick={() => deleteBatch(b.id)}
                        className="px-3 py-1 text-xs rounded-lg border border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        Delete
                      </PermissionButton>
                    </div>
                  </TableActions></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between items-center px-6 py-4 text-sm text-gray-400">
            <div>
              Page {currentPage} of {totalPages}
            </div>

            <div className="flex gap-2">
              <PermissionButton permissionModule="production"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1 rounded-lg border border-white/10 disabled:opacity-30"
              >
                Prev
              </PermissionButton>

              <PermissionButton permissionModule="production"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1 rounded-lg border border-white/10 disabled:opacity-30"
              >
                Next
              </PermissionButton>
            </div>
          </div>
        </div>

        {handoverBatchId && (
          <Modal onClose={() => setHandoverBatchId(null)}>
            <h2 className="text-xl font-serif mb-6">Shift Handover</h2>

            <div className="space-y-4">
              <label className="block text-sm space-y-2"><span>Producing department</span><SearchableSelect value={producingDepartment} onChange={e => setProducingDepartment(e.target.value)} className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"><option value="">Select Production department</option>{departments.filter(d => d.parent_id && d.module==='production' && d.is_active && (d.all_branches || d.branch_ids.includes(Number(selectedBranch || branchId)))).map(d => <option key={d.id} value={d.id}>{d.parent_name} / {d.name}</option>)}</SearchableSelect></label>
              {/* Next Staff */}
              <SearchableSelect
                value={handoverStaff}
                onChange={(e) => setHandoverStaff(e.target.value)}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10 w-full"
              >
                <option value="">Hand over to staff</option>
                {staffList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} — {s.role}
                  </option>
                ))}
              </SearchableSelect>

              {/* Next Shift */}
              <SearchableSelect
                value={handoverShift}
                onChange={(e) => setHandoverShift(e.target.value)}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10 w-full"
              >
                <option>Morning</option>
                <option>Evening</option>
                <option>Night</option>
              </SearchableSelect>

              {/* Note */}
              <textarea
                value={handoverNote}
                onChange={(e) => setHandoverNote(e.target.value)}
                placeholder="Handover note (optional)"
                rows={3}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10 w-full resize-none"
              />
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <PermissionButton permissionModule="production"
                onClick={() => setHandoverBatchId(null)}
                className="px-6 py-2 rounded-xl border border-white/20"
              >
                Cancel
              </PermissionButton>

              <PermissionButton permissionModule="production"
                disabled={!handoverStaff}
                onClick={async () => {
                  try {
                    const res = await fetch(
                      "/api/production/handover",
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        credentials: "include",
                        body: JSON.stringify({
                          batch_id: handoverBatchId,
                          to_staff_id: Number(handoverStaff),
                          from_staff_id: batches.find(
                            (b) => b.id === handoverBatchId,
                          )?.staff_id,
                          to_shift: handoverShift,
                          note: handoverNote || "Shift handover",
                        }),
                      },
                    );

                    const data = await res.json();

                    if (data.status !== "success") {
                      alert(data.message);
                      return;
                    }

                    // 🔥 refresh batches after handover
                    fetchBatches();

                    // reset UI
                    setHandoverBatchId(null);
                    setHandoverStaff("");
                    setHandoverNote("");
                  } catch (err) {
                    console.error(err);
                    alert("Handover failed");
                  }
                }}
                className="px-6 py-2 rounded-xl bg-amber-400 text-black font-medium"
              >
                Confirm Handover
              </PermissionButton>
            </div>
          </Modal>
        )}

        {/* Create Batch Modal */}
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <h2 className="text-xl font-serif mb-6">{selectedBatch ? "Update Production Batch" : "Create Production Batch"}</h2>

            <div className="space-y-4">
              {/* Recipe */}
              <SearchableSelect
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10 w-full"
                onChange={(e) =>
                  setSelectedRecipe(
                    recipes.find((r) => r.id === Number(e.target.value)),
                  )
                }
              >
                <option value="">Select Recipe</option>
                {recipes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.product}
                  </option>
                ))}
              </SearchableSelect>

              {/* Quantity */}
              {selectedRecipe && (
                <p className="text-sm text-amber-400">Finished-stock receiving department: {inventory.find(item => item.id === selectedRecipe.product_item_id)?.department || 'Assign a department to the finished item first'}</p>
              )}
              {selectedRecipe && (
                <input
                  type="number"
                  placeholder={`Quantity (${selectedRecipe.baseUnit})`}
                  value={batchQty}
                  onChange={(e) => setBatchQty(e.target.value)}
                  className="px-4 py-2 rounded-lg bg-black/40 border border-white/10 w-full"
                />
              )}

              {user?.role === "super_admin" && (
                <SearchableSelect
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(Number(e.target.value))}
                  className="px-4 py-2 rounded-lg bg-black/40 border border-white/10 w-full"
                >
                  <option value="">Select Branch</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </SearchableSelect>
              )}

              <SearchableSelect
                value={assignedStaff}
                onChange={(e) => setAssignedStaff(Number(e.target.value))}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10 w-full"
              >
                <option value="">Assign Staff</option>
                {staffList
                  .filter((s) =>
                    user?.role === "super_admin"
                      ? true
                      : s.branch_id === branchId,
                  )
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} — {s.role}
                    </option>
                  ))}
              </SearchableSelect>

              <SearchableSelect
                value={shift}
                onChange={(e) => setShift(e.target.value)}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10 w-full"
              >
                <option>Morning</option>
                <option>Evening</option>
                <option>Night</option>
              </SearchableSelect>

              {/* Deduction Preview */}
              {deductionPreview.length > 0 && (
                <div className="mt-4 rounded-xl bg-black/40 border border-white/10 p-4">
                  <p className="text-sm text-gray-400 mb-2">
                    Auto Deduction Preview
                  </p>

                  {deductionPreview.map((d, i) => {
                    const insufficient = d.required > d.available;
                    return (
                      <div
                        key={i}
                        className="flex justify-between text-sm py-1"
                      >
                        <span>{d.item || "-"}<small className="block text-gray-400">From {d.department}</small></span>
                        <span>
                          {d.required.toFixed(2)} {d.unit} /{" "}
                          <span
                            className={
                              insufficient ? "text-red-400" : "text-emerald-400"
                            }
                          >
                            {d.available}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <PermissionButton permissionModule="production"
                onClick={() => setShowModal(false)}
                className="px-6 py-2 rounded-xl border border-white/20"
              >
                Cancel
              </PermissionButton>
              <PermissionButton permissionModule="production"
                disabled={!producingDepartment || Number(batchQty)<=0 || (!selectedBatch && (hasInsufficientStock || !selectedRecipe))}
                onClick={selectedBatch ? updateBatch : createBatch}
                className={`px-6 py-2 rounded-xl font-medium ${
                  hasInsufficientStock
                    ? "bg-gray-600 text-gray-300"
                    : "bg-amber-400 text-black"
                }`}
              >
                Create Batch
              </PermissionButton>
            </div>
          </Modal>
        )}
      </div>
      {detail && <ProductionBatchDetails batch={detail} onClose={() => setDetail(null)} />}
    </DashboardLayout>
  );
}

// ================= MODAL =================
function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center">
      <div className="bg-[#0b0b0f] p-6 rounded-xl">
        <PermissionButton permissionModule="production" onClick={onClose}>X</PermissionButton>
        {children}
      </div>
    </div>
  );
}
